/**
 * AppDivider - "Or" separator with text
 *
 * Design System Layer 2: Consistent divider with centered text
 * Used in auth flows for "or continue with..." patterns
 *
 * Matches Rails: Ui::DividerComponent
 */

import type { LocalizedText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';

export interface AppDividerProps {
  /** Text to display in the center */
  text?: LocalizedText;
  /** Additional classes */
  className?: string;
}

/**
 * AppDivider - Horizontal line with optional centered text
 *
 * @example
 * // Simple divider
 * <AppDivider />
 *
 * // With "or" text (common in auth)
 * <AppDivider text="or" />
 *
 * // Localized
 * <AppDivider text={t("modal.or")} />
 */
export function AppDivider({ text, className }: AppDividerProps) {
  if (!text) {
    return <div className={cn('h-px w-full bg-border', className)} role="separator" />;
  }

  return (
    <div className={cn('relative my-6', className)} role="separator">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-border" />
      </div>
      <div className="relative flex justify-center text-sm">
        <span className="bg-background px-4 text-muted-foreground">{text}</span>
      </div>
    </div>
  );
}
